import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ConfigProvider, Input, Select } from 'antd'
import { useTranslation } from "react-i18next";
import { Button } from "@mui/material";
import { useMutation, useQuery } from 'react-query'
import { getCountryList, registerDetail, validateFields } from "api";
import OrderForm from "./OrderForm";
import { showErrorMessage } from "utils/showMessage";
import { useDispatch, useSelector } from "react-redux";
import CircularProgress from '@mui/material/CircularProgress';
import { VALIDATE_FIELDS } from "constant";
import PhoneInput, {locale} from 'antd-phone-input'

const BillingInfoPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate()

  const { state } = useLocation()
  const token = useSelector((state) => state.subscriptions.registrationToken)

  const login_res = state?.login_res;

  const [registrationToken, setRegistrationToken] = useState('')
  const [loading, setLoading] = useState(false)

  // Schema di validazione con Yup

  const companyValidationSchema = Yup.object({
    user_type: Yup.string().required(t("Required")),
    company_name: Yup.string().required(t("Required")),
    vat_number: Yup.string()
      .required(t("Required"))
      .matches(/^[a-z0-9]+$/i, t("invalid vat_number"))
      .min(8, t("Min value 8"))
      .max(15, t("Max value 15")),

    tax_code: Yup.string()
      .matches(/^[a-z0-9]+$/i, t("invalid tax_code"))
      .min(6, t("Min value 6"))
      .max(20, t("Max value 20")),

    sdi_receiver_code: Yup.string()
      .matches(/^[a-z0-9]+$/i, t("invalid sdi_receiver_code"))
      .min(7, t("Min value 7"))
      .max(7, t("Max value 7")),

    pec_address: Yup.string()
      .email(t("Invalid email"))
      .required(t("Required")),

    address: Yup.string().required(t("Required")),

    municipality: Yup.string()
      .required(t("Required"))
      .min(3, t("Min value 3"))
      .max(50, t("Max value 50")),

    zip_code: Yup.string()
      .required(t("Required"))
      .matches(/^[0-9]+$/, t("invalid zipcode"))
      .min(5, t("Min value 5"))
      .max(5, t("Max value 5")),

    province: Yup.string()
      .required(t("Required"))
      .matches(/^[a-z]+$/i, t("invalid province"))
      .min(2, t("Min value 2"))
      .max(50, t("Max value 50")),

    country_id: Yup.string().required(),
    email: Yup.string()
      .email(t("Invalid email"))
      .required(t("Required")),

    phone: Yup.string()
      .matches(/^\+\d{1,3}\d{6,14}$/, t("Invalid phone number, insert prefix es. +39 and no spaces"))
      .required(t("Required"))


  });

  const privateValidationSchema = Yup.object({
    user_type: Yup.string().required(t("Required")),
    name: Yup.string().required(t("Required")),
    surname: Yup.string().required(t("Required")),
    fiscal_code: Yup.string().required(t("Required")).matches(
      /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/,
      t("Invalid fiscal code format")
    ),

    address: Yup.string().required(t("Required")),
    // number: Yup.string().required(t("Required")),
    municipality: Yup.string()
      .required(t("Required"))
      .min(3, t("Min value 3"))
      .max(50, t("Max value 50")),
    zip_code: Yup.string()
      .required(t("Required"))
      .matches(/^[0-9]+$/, t("invalid zipcode"))
      .min(5, t("Min value 5"))
      .max(5, t("Max value 5")),
    province: Yup.string()
      .required(t("Required"))
      .matches(/^[a-z]+$/i, t("invalid province"))
      .min(2, t("Min value 2"))
      .max(2, t("Max value 2")),
    country_id: Yup.string().required(),


    email: Yup.string()
      .email(t("Invalid email"))
      .required(t("Required")),
    phone: Yup.string()
      .matches(/^\+\d{1,3}\d{6,14}$/, t("Invalid phone number, insert prefix es. +39 and no spaces"))
      .required(t("Required"))

  });

  const companyValues = {
    user_type: "COMPANY",
    company_name: "",
    vat_number: "",
    tax_code: "",
    sdi_receiver_code: "",
    pec_address: "",
    address: "",
    // number: "",
    municipality: "",
    zip_code: "",
    province: "",
    country_id: 111,
    email: "",
    phone: "",

  }


  const userValues = {
    user_type: "PRIVATE",
    fiscal_code: "",
    address: "",
    // number: "",
    municipality: "",
    zip_code: "",
    province: "",
    country_id: 111,
    name: "",
    surname: "",
    email: "",
    phone: "",

  }


  const formik = useFormik({
    initialValues: () => (formik.values.user_type === "COMPANY" ? companyValues : userValues),
    validationSchema: () => (formik.values.user_type === "COMPANY" ? companyValidationSchema : privateValidationSchema),
    onSubmit: () => {
      setLoading(true)
      onValidateFields()

    },
  });

  const onValidateFields = async () => {

    if(formik.values.user_type === 'COMPANY'){
      try {
        await validateFields(registrationToken, { field_name: VALIDATE_FIELDS.company_name, value: formik.values.company_name })
        
      } catch (error) {
        setLoading(false)
        if (error?.response?.data?.errors[0]?.code === 1006) {
          showErrorMessage(t('error messages.Company name already exists!'))
        } else {
          showErrorMessage(t('error messages.Internal server error'))
        }
        return;
      }

      try {
        await validateFields(registrationToken, { field_name: VALIDATE_FIELDS.vat_number, value: formik.values.vat_number })
        
      } catch (error) {
        setLoading(false)
        if (error?.response?.data?.errors[0]?.code === 1006) {
          showErrorMessage(t('error messages.Vat already exists'))
        } else {
          showErrorMessage(t('error messages.Internal server error'))
        }
        return;
      }
    }

    try {

      const res = await validateFields(registrationToken, { field_name: VALIDATE_FIELDS.company_email, value: formik.values.email })

      if (res?.resData?.data) {
        
        try {
          const res = await validateFields(registrationToken, { field_name: VALIDATE_FIELDS.phone, value: formik.values.phone })
          if (res?.resData?.data) {
            setLoading(false)
            registrationDetail.mutate(formik.values)
          }
        } catch (error) {
          setLoading(false)
          if (error?.response?.data?.errors[0]?.code === 1006) {
            showErrorMessage(t('error messages.CompanyPhone already exists!'))
          } else {
            showErrorMessage(t('error messages.Internal server error'))
          }
          return;
        }


      }
    } catch (error) {

      setLoading(false)
      if (error?.response?.data?.errors[0]?.code === 1006) {
        showErrorMessage(t('error messages.Email already exists'))
      } else {
        showErrorMessage(t('error messages.Internal server error'))
      }
      return;
    }
  }


  const validate = useMutation({
    mutationKey: 'register/validate-company-email',
    mutationFn: (email) => validateFields(registrationToken, { field_name: VALIDATE_FIELDS.company_email, value: email }),
    onSuccess: (res) => {
      setLoading(false)
      if (res?.resData?.data) {

        registrationDetail.mutate(formik.values)
      }


    },
    onError: (err) => {

      setLoading(false)
      if (err?.response?.data?.errors[0]?.code === 1006) {
        showErrorMessage(t('error messages.Email already exists'))
      } else {
        showErrorMessage(t('error messages.Internal server error'))
      }
    }
  })

  const registrationDetail = useMutation({
    mutationKey: 'registerDetail',
    mutationFn: (values) => registerDetail(values, registrationToken),
    onSuccess: (res) => {
      setLoading(false)
      navigate('/pay', { state: { login_res: login_res } })


    },
    onError: (err) => {
      setLoading(false)
      showErrorMessage(err.message)
    }
  })

  const { data } = useQuery({
    queryKey: ['countries-list'],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (!registrationToken) return null;

      const res = await getCountryList(registrationToken)
      return res
    },
    enabled: registrationToken?.length > 0,
  })



  const countryOptions = useMemo(() => {
    return data
      ? data?.resData?.data?.map((country) => ({
        value: country.id,
        label: country.name,
      }))
      : [];
  }, [data]);



  useEffect(() => {
    formik.setFieldValue('user_type', 'COMPANY')
    formik.setFieldValue('country_id', 111)
  }, [])

  useEffect(() => {

    //console.log('token', token);


    if (token && token?.length > 0) {
      localStorage.setItem('registrationToken', token)
      setRegistrationToken(token)
    } else {
      setRegistrationToken(localStorage.getItem('registrationToken'))
    }

  }, [token])




  return (
    <div className="grid grid-cols-3 max-h-[100vh] overflow-hidden w-screen bg-[#F7F7F8] p-2">
      {/* Form */}
      <div className="max-h-[100vh] col-span-2 justify-left w-full h-full bg-white rounded-2xl relative">
        <div
          className="max-h-[100vh] overflow-y-auto pb-20">
          <div className=" p-[40px]">
            <p className="text-sm text-[#828282] font-normal">Step 3 {t("OF").toLowerCase()} 5</p>
            <p className="text-3xl text-black font-semibold">{t("Billing.Title")}</p>
            <p className="text-base text-[#828282] font-normal">{t("Billing.Subtitle")}</p>
            
            <form onSubmit={formik.handleSubmit} className="mt-[40px]">
              
              <div>
                <p className="text-sm font-normal text-[#595961]">{t("Billing.UserType")}</p>
                
                
                <Select
                  defaultValue={0}
                  name="user_type"
                  value={formik.values.user_type}
                  onChange={(value) => formik.setFieldValue('user_type', value)}
                  options={[
                    { value: 'COMPANY', label: t('Billing.Company') },
                    { value: 'PRIVATE', label: t('Billing.PrivateUser') },
                  
                  ]}
                  style={{
                    width: "100%",
                    
                  }}
                />
                
                
                {formik.touched.user_type && formik.errors.user_type && (
                  <p className="text-red-500 text-xs mt-1">{formik.errors.user_type}</p>
                )}
                
                {formik.values.user_type === 'PRIVATE' && (
                  <>
                    <p className="text-sm font-normal text-[#595961] mt-4">{t("Billing.Name")}</p>
                    <Input
                      type="text"
                      name="name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      style={{
                        width: "100%",
                        border: "none",
                        borderBottom: "2px solid #E3E3E8",
                        borderRadius: "0px",
                      }}
                    />
                    {formik.touched.name && formik.errors.name && (
                      <p className="text-red-500 text-xs mt-1">{formik.errors.name}</p>
                    )}
                    
                    <p className="text-sm font-normal text-[#595961]">{t("Billing.Surname")}</p>
                    <Input
                      type="text"
                      name="surname"
                      value={formik.values.surname}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      style={{
                        width: "100%",
                        border: "none",
                        borderBottom: "2px solid #E3E3E8",
                        borderRadius: "0px",
                      }}
                    />
                    {formik.touched.surname && formik.errors.surname && (
                      <p className="text-red-500 text-xs mt-1">{formik.errors.surname}</p>
                    )}
                    <>
                      <p className="text-sm font-normal text-[#595961]">{t("Billing.CodiceFiscale")}</p>
                      <Input
                        type="text"
                        name="fiscal_code"
                        value={formik.values.fiscal_code}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        style={{
                          width: "100%",
                          border: "none",
                          borderBottom: "2px solid #E3E3E8",
                          borderRadius: "0px",
                        }}
                      />
                      {formik.touched.fiscal_code && formik.errors.fiscal_code && (
                        <p className="text-red-500 text-xs mt-1">{formik.errors.fiscal_code}</p>
                      )}</>
                  
                  </>
                
                )}
                
                
                {formik.values.user_type === 'COMPANY' && (
                  <>
                    <p className="text-sm font-normal text-[#595961] mt-4">{t("Billing.RagioneSociale")}</p>
                    <Input
                      type="text"
                      name="company_name"
                      value={formik.values.company_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      style={{
                        width: "100%",
                        border: "none",
                        borderBottom: "2px solid #E3E3E8",
                        borderRadius: "0px",
                      }}
                    />
                    {formik.touched.company_name && formik.errors.company_name && (
                      <p className="text-red-500 text-xs mt-1">{formik.errors.company_name}</p>
                    )}
                    
                    <p className="text-sm font-normal text-[#595961] mt-4">{t("Billing.PIva")}</p>
                    <Input
                      type="text"
                      name="vat_number"
                      value={formik.values.vat_number}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      style={{
                        width: "100%",
                        border: "none",
                        borderBottom: "2px solid #E3E3E8",
                        borderRadius: "0px",
                      }}
                    />
                    {formik.touched.vat_number && formik.errors.vat_number && (
                      <p className="text-red-500 text-xs mt-1">{formik.errors.vat_number}</p>
                    )}
                    
                    <p className="text-sm font-normal text-[#595961] mt-4">{t("Billing.CodiceFiscale")}</p>
                    <Input
                      type="text"
                      name="tax_code"
                      value={formik.values.tax_code}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      style={{
                        width: "100%",
                        border: "none",
                        borderBottom: "2px solid #E3E3E8",
                        borderRadius: "0px",
                      }}
                    />
                    {formik.touched.tax_code && formik.errors.tax_code && (
                      <p className="text-red-500 text-xs mt-1">{formik.errors.tax_code}</p>
                    )}
                  
                  
                  </>
                )}
                
                
                {formik.values.user_type === 'COMPANY' && (
                  <>
                    <p className="text-sm font-normal text-[#595961] mt-4">{t("Billing.CodiceDestinatarioSDI")}</p>
                    <Input
                      type="text"
                      name="sdi_receiver_code"
                      value={formik.values.sdi_receiver_code}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      style={{
                        width: "100%",
                        border: "none",
                        borderBottom: "2px solid #E3E3E8",
                        borderRadius: "0px",
                      }}
                    />
                    {formik.touched.sdi_receiver_code && formik.errors.sdi_receiver_code && (
                      <p className="text-red-500 text-xs mt-1">{formik.errors.sdi_receiver_code}</p>
                    )}
                    
                    <p className="text-sm font-normal text-[#595961] mt-4">{t("Billing.PEC")}</p>
                    <Input
                      type="text"
                      name="pec_address"
                      value={formik.values.pec_address}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      style={{
                        width: "100%",
                        border: "none",
                        borderBottom: "2px solid #E3E3E8",
                        borderRadius: "0px",
                      }}
                    />
                    {formik.touched.pec_address && formik.errors.pec_address && (
                      <p className="text-red-500 text-xs mt-1">{formik.errors.pec_address}</p>
                    )}
                  
                  </>
                )}
                
                
                <p className="text-base font-semibold text-black mt-8">{t("Billing.Office")}</p>
                
                
                <div className="flex gap-4">
                  <div className="w-full">
                    <p className="text-sm font-normal text-[#595961] mt-4">{t("Billing.Address")}</p>
                    <Input
                      type="text"
                      name="address"
                      value={formik.values.address}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      style={{
                        width: "100%",
                        border: "none",
                        borderBottom: "2px solid #E3E3E8",
                        borderRadius: "0px",
                      }}
                    />
                    {formik.touched.address && formik.errors.address && (
                      <p className="text-red-500 text-xs mt-1">{formik.errors.address}</p>
                    )}</div>
                  
                  
                  {/* <div>
                <p className="text-sm font-normal text-[#595961]">{t("Billing.StreetNumber")}</p>
                <Input
                  type="text"
                  name="number"
                  value={formik.values.number}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  style={{
                    width: "100%",
                    border: "none",
                    borderBottom: "2px solid #E3E3E8",
                    borderRadius: "0px",
                  }}
                />
                {formik.touched.number && formik.errors.number && (
                  <p className="text-red-500 text-xs mt-1">{formik.errors.number}</p>
                )}
              </div> */}
                
                </div>
                
                
                <div className="flex gap-4">
                  <div className="w-full">
                    <p className="text-sm font-normal text-[#595961] mt-4">{t("Billing.City")}</p>
                    <Input
                      type="text"
                      name="municipality"
                      value={formik.values.municipality}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      style={{
                        width: "100%",
                        border: "none",
                        borderBottom: "2px solid #E3E3E8",
                        borderRadius: "0px",
                      }}
                    />
                    {formik.touched.municipality && formik.errors.municipality && (
                      <p className="text-red-500 text-xs mt-1">{formik.errors.municipality}</p>
                    )}
                  </div>
                  
                  
                  <div className="w-full">
                    <p className="text-sm font-normal text-[#595961] mt-4">{t("Billing.zipCode")}</p>
                    <Input
                      type="text"
                      name="zip_code"
                      value={formik.values.zip_code}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      style={{
                        width: "100%",
                        border: "none",
                        borderBottom: "2px solid #E3E3E8",
                        borderRadius: "0px",
                      }}
                    />
                    {formik.touched.zip_code && formik.errors.zip_code && (
                      <p className="text-red-500 text-xs mt-1">{formik.errors.zip_code}</p>
                    )}
                  </div>
                
                
                </div>
                
                
                <div className="flex gap-4">
                  
                  
                  <div className="w-full">
                    <p className="text-sm font-normal text-[#595961] mt-4">{t("Billing.Province")}</p>
                    <Input
                      type="text"
                      name="province"
                      value={formik.values.province}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      style={{
                        width: "100%",
                        border: "none",
                        borderBottom: "2px solid #E3E3E8",
                        borderRadius: "0px",
                      }}
                    />
                    {formik.touched.province && formik.errors.province && (
                      <p className="text-red-500 text-xs mt-1">{formik.errors.province}</p>
                    )}
                  </div>
                  <div className="w-full">
                    <p className="text-sm font-normal text-[#595961] mt-4">{t("Billing.Country")}</p>
                    
                    
                    <Select
                      defaultValue={111}
                      name="country_id"
                      value={formik.values.country_id}
                      onChange={(value) => formik.setFieldValue('country_id', value)}
                      options={countryOptions}
                      style={{
                        width: "100%",
                        
                      }}
                    />
                    
                    {formik.touched.country_id && formik.errors.country_id && (
                      <p className="text-red-500 text-xs mt-1">{formik.errors.country_id}</p>
                    )}
                  </div>
                
                </div>
                
                
                <div className="flex gap-4">
                  
                  <div className="w-full">
                    <p className="text-sm font-normal text-[#595961] mt-4">Email</p>
                    <Input
                      type="text"
                      name="email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      style={{
                        width: "100%",
                        border: "none",
                        borderBottom: "2px solid #E3E3E8",
                        borderRadius: "0px",
                      }}
                    />
                    {formik.touched.email && formik.errors.email && (
                      <p className="text-red-500 text-xs mt-1">{formik.errors.email}</p>
                    )}
                  </div>
                  <div className="w-full">
                    <p className="text-sm font-normal text-[#595961] mt-4">{t("Billing.Telephone")}</p>
                    <ConfigProvider locale={locale("itIT")}>
                      <PhoneInput
                        enableSearch
                        className="phone-custom-registration-select"
                        name="phone"
                        value={formik.values.phone}
                        onChange={(value) => {
                          formik.setFieldValue('phone', `+${value.countryCode}${value.areaCode}${value.phoneNumber}`)
                        }}
                        style={{
                          width: "100%",
                          border: "none",
                          borderBottom: "2px solid #E3E3E8",
                          borderRadius: "0px",
                        }}
                      />
                    </ConfigProvider>
                    {formik.touched.phone && formik.errors.phone && (
                      <p className="text-red-500 text-xs mt-1">{formik.errors.phone}</p>
                    )}
                  
                  </div>
                
                </div>
              
              
              </div>
              
              
              {/* Pulsanti */}
              <div className="mt-[58px] flex justify-end absolute bottom-0 pb-6 pt-4 bg-white w-[calc(100%_-_80px)]">
                {/* <Button
              onClick={() => {
                navigate(-1)
              }}
              type="button"
              variant="outlined"
              sx={{
                width: 124,
                borderColor: "#919191",
                fontSize: "16px",
                textTransform: "none",
                color: "#919191",
                paddingLeft: "22px",
                paddingRight: "22px",
                fontWeight: 600,
                marginRight: 2,
              }}
            >
              {t("Signup.Back")}
            </Button> */}
                
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!formik.isValid || !formik.dirty || loading}
                  sx={{
                    width: loading ? 148 : 124,
                    paddingLeft: "22px",
                    paddingRight: "22px",
                    fontWeight: 600,
                    // backgroundColor: formik.isValid ? "#0049EE" : "#A0A0A0",
                    fontSize: "16px",
                    textTransform: "none",
                    boxShadow: 'none',
                    ':hover': {
                      backgroundColor: '#0049EE',
                      boxShadow: 'none',
                    }
                  }}
                >
                  {t("Otp.Buy")}
                  
                  {loading && (<CircularProgress size="20px" style={{ marginLeft: 8, color: 'white' }} />)}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
      
      {/* Sidebar con dettagli */}
      <OrderForm showDiscount={true} login_res={login_res} />
    </div>
  );
};

export default BillingInfoPage;
